import {
  API_CONFIG,
  API_ENDPOINTS,
  ERROR_MESSAGES,
  POLLING,
} from "../utils/constants";
import type {
  ApiResponse,
  User,
  UserRequest,
  UserUpdateRequest,
  VideoGenerationRequest,
  VideoGenerationResponse,
  ImageGenerationRequest,
  ImageGenerationResponse,
  VideoStatusResponse,
  VideoDeleteRequest,
  VideoFavoriteRequest,
  VideoFavoriteResponse,
  VideoExportRequest,
  VideoExportResponse,
  VideosListRequest,
  VideoListResponse,
  PollStatusOptions,
  ModelsRequest,
  ModelsResponse,
  TemplatesListRequest,
  TemplatesListResponse,
} from "../types/api";

class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({
          status: "error" as const,
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));
        throw new ApiError(errorData.message, response.status, errorData.data);
      }

      const data: ApiResponse<T> = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new ApiError(ERROR_MESSAGES.TIMEOUT_ERROR, 408);
        }
        if (error.message.includes("Failed to fetch")) {
          throw new ApiError(ERROR_MESSAGES.NETWORK_ERROR, 0);
        }
        throw new ApiError(error.message, 500);
      }

      throw new ApiError(ERROR_MESSAGES.UNKNOWN_ERROR, 500);
    }
  }

  // User Management
  async getUser(request: UserRequest): Promise<User> {
    const response = await this.makeRequest<User>(API_ENDPOINTS.USER.GET, {
      method: "POST",
      body: JSON.stringify(request),
    });

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  async updateUser(request: UserUpdateRequest): Promise<User> {
    const response = await this.makeRequest<User>(API_ENDPOINTS.USER.UPDATE, {
      method: "PUT",
      body: JSON.stringify(request),
    });

    if (response.status === "error") {
      throw new ApiError(response.message, 400);
    }

    return response.data!;
  }

  async resetMonthlyUsage(
    request: UserRequest
  ): Promise<User["limits"] & { plan: string }> {
    const response = await this.makeRequest<User["limits"] & { plan: string }>(
      API_ENDPOINTS.USER.RESET_MONTHLY,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  // Video Management
  async getVideos(request: VideosListRequest): Promise<VideoListResponse> {
    const params = new URLSearchParams({
      clerk_id: request.clerk_id,
      ...(request.page && { page: request.page.toString() }),
      ...(request.per_page && { per_page: request.per_page.toString() }),
    });

    const response = await this.makeRequest<VideoListResponse>(
      `${API_ENDPOINTS.VIDEOS.LIST}?${params}`,
      { method: "GET" }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  async deleteVideo(
    request: VideoDeleteRequest
  ): Promise<{ video_id: string; status: string }> {
    const response = await this.makeRequest<{
      video_id: string;
      status: string;
    }>(API_ENDPOINTS.VIDEOS.DELETE, {
      method: "DELETE",
      body: JSON.stringify(request),
    });

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  async toggleFavorite(
    request: VideoFavoriteRequest
  ): Promise<VideoFavoriteResponse> {
    const response = await this.makeRequest<VideoFavoriteResponse>(
      API_ENDPOINTS.VIDEOS.FAVORITE,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  // Video Generation
  async createVideo(
    request: VideoGenerationRequest
  ): Promise<VideoGenerationResponse> {
    const response = await this.makeRequest<VideoGenerationResponse>(
      API_ENDPOINTS.GENERATE.CREATE,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 403, response.data);
    }

    return response.data!;
  }

  // Image Generation
  async createImage(
    request: ImageGenerationRequest
  ): Promise<ImageGenerationResponse> {
    const response = await this.makeRequest<any>(
      API_ENDPOINTS.GENERATE.CREATE,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 403, response.data);
    }

    // For image generation, the API returns the data directly (not nested under 'data')
    // Check if the response itself contains image data
    if ((response as any).images) {
      return response as ImageGenerationResponse;
    }

    return response.data!;
  }

  async getVideoStatus(operation_id: string): Promise<VideoStatusResponse> {
    const params = new URLSearchParams({ operation_id });
    const response = await this.makeRequest<VideoStatusResponse>(
      `${API_ENDPOINTS.GENERATE.STATUS}?${params}`,
      { method: "GET" }
    );

    if (response.status === "error") {
      // Check if this is a content policy violation with deleted status
      if (response.data?.status === "deleted") {
        // Return the status data so it can be handled by polling logic
        return response.data;
      }
      // Check if this is a content policy violation error
      if (response.data?.error && response.data?.status === "failed") {
        throw new ApiError(response.data.error, 400, response.data);
      }
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  // Video Export
  async exportVideo(request: VideoExportRequest): Promise<VideoExportResponse> {
    const response = await this.makeRequest<VideoExportResponse>(
      API_ENDPOINTS.EXPORT,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );

    if (response.status === "error") {
      throw new ApiError(
        response.message,
        response.message.includes("processing") ? 202 : 404
      );
    }

    return response.data!;
  }

  // Models
  async getModels(request: ModelsRequest): Promise<ModelsResponse> {
    const params = new URLSearchParams({ type: request.type });
    const response = await this.makeRequest<ModelsResponse>(
      `${API_ENDPOINTS.MODELS}?${params}`,
      { method: "GET" }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  // Templates
  async getTemplates(request: TemplatesListRequest = {}): Promise<TemplatesListResponse> {
    const params = new URLSearchParams();
    if (request.page !== undefined) {
      params.append("page", request.page.toString());
    }
    if (request.per_page !== undefined) {
      params.append("per_page", request.per_page.toString());
    }

    const queryString = params.toString();
    const endpoint = queryString
      ? `${API_ENDPOINTS.TEMPLATES}?${queryString}`
      : API_ENDPOINTS.TEMPLATES;

    const response = await this.makeRequest<TemplatesListResponse>(
      endpoint,
      { method: "GET" }
    );

    if (response.status === "error") {
      throw new ApiError(response.message, 404);
    }

    return response.data!;
  }

  // Polling utilities
  async pollVideoStatus(
    options: PollStatusOptions
  ): Promise<VideoStatusResponse> {
    const {
      operation_id,
      onProgress,
      onComplete,
      onError,
      maxRetries = POLLING.MAX_RETRIES,
      interval = POLLING.VIDEO_STATUS,
    } = options;

    let retries = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getVideoStatus(operation_id);

          if (status.status === "processing") {
            onProgress?.(status);

            if (retries >= maxRetries) {
              const error = "Video generation timed out";
              onError?.(error);
              reject(new ApiError(error, 408));
              return;
            }

            retries++;
            setTimeout(poll, interval);
          } else if (status.status === "completed") {
            onComplete?.(status);
            resolve(status);
          } else if (status.status === "failed") {
            const error = status.error || "Video generation failed";
            onError?.(error);
            reject(new ApiError(error, 500));
          } else if (status.status === "deleted") {
            // Handle content policy violations that result in deleted status
            const error =
              status.error ||
              "Video generation was deleted due to policy violation";
            onError?.(error);
            reject(new ApiError(error, 400, status));
          }
        } catch (error) {
          const errorMessage =
            error instanceof ApiError
              ? error.message
              : "Unknown error occurred";
          onError?.(errorMessage);
          reject(error);
        }
      };

      poll();
    });
  }
}

// Custom error class for API errors
export class ApiError extends Error {
  public status: number;
  public data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
